import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Image, Pencil, Plus, Trash2 } from 'lucide-react';
import { tarjetasReceptorasApi } from '@/services/tarjetasReceptorasApi';
import { resolveImageUrl } from '@/utils/formatters';
import { StockBadge } from '@/components/shared/StockBadge';
import type {
  TarjetaReceptoraCreateRequest,
  TarjetaReceptoraDto,
} from '@/types/tarjetaReceptora';

const QUERY_KEY = ['tarjetas-receptoras'] as const;
const emptyForm: TarjetaReceptoraCreateRequest = { codigo: '', nombre: '' };

export function TarjetasReceptorasSection() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<TarjetaReceptoraCreateRequest>({ ...emptyForm });

  const [showImg, setShowImg] = useState(false);
  const [imgItem, setImgItem] = useState<TarjetaReceptoraDto | null>(null);
  const [imgFile, setImgFile] = useState<File | null>(null);
  const [imgPreview, setImgPreview] = useState('');
  const imgInputRef = useRef<HTMLInputElement>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: tarjetasReceptorasApi.findAll,
  });

  const saveMutation = useMutation({
    mutationFn: (data: TarjetaReceptoraCreateRequest) =>
      editingId ? tarjetasReceptorasApi.update(editingId, data) : tarjetasReceptorasApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setShowModal(false);
      setEditingId(null);
      setForm({ ...emptyForm });
      toast.success(editingId ? 'Tarjeta actualizada' : 'Tarjeta creada');
    },
    onError: () => toast.error('Error al guardar tarjeta'),
  });

  const deleteMutation = useMutation({
    mutationFn: tarjetasReceptorasApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Tarjeta eliminada');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      tarjetasReceptorasApi.uploadImage(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setShowImg(false);
      setImgItem(null);
      setImgFile(null);
      setImgPreview('');
      toast.success('Imagen actualizada');
    },
    onError: () => toast.error('Error al subir imagen'),
  });

  const openEdit = (t: TarjetaReceptoraDto) => {
    setEditingId(t.id);
    setForm({ codigo: t.codigo, nombre: t.nombre });
    setShowModal(true);
  };

  const openImg = (t: TarjetaReceptoraDto) => {
    setImgItem(t);
    setImgFile(null);
    setImgPreview(resolveImageUrl(t.imagen) ?? '');
    setShowImg(true);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 mt-12">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Tarjetas Receptoras</h2>
        <button
          onClick={() => {
            setEditingId(null);
            setForm({ ...emptyForm });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          <Plus size={16} />
          Nueva Tarjeta
        </button>
      </div>

      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Imagen</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nombre</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-500">Cargando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-500">No hay tarjetas</td></tr>
            ) : (
              items.map((t) => (
                <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {t.imagen ? (
                      <img src={resolveImageUrl(t.imagen)!} alt={t.nombre} className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center"><Image size={16} className="text-gray-400" /></div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">{t.codigo}</td>
                  <td className="px-4 py-3 text-sm font-medium">{t.nombre}</td>
                  <td className="px-4 py-3"><StockBadge cantidad={t.cantidadTotal} desglose={t.stockPorAlmacen} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openImg(t)} className="text-purple-500 hover:text-purple-700" title="Imagen"><Image size={16} /></button>
                      <button onClick={() => openEdit(t)} className="text-blue-600 hover:text-blue-800"><Pencil size={16} /></button>
                      <button onClick={() => deleteMutation.mutate(t.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3 mb-4">
        {isLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : items.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No hay tarjetas</p>
        ) : (
          items.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex gap-3 items-center">
                {t.imagen ? (
                  <img src={resolveImageUrl(t.imagen)!} alt={t.nombre} className="w-12 h-12 object-cover rounded-lg border" />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 rounded-lg border flex items-center justify-center"><Image size={18} className="text-gray-400" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{t.nombre}</p>
                  <p className="text-xs text-gray-500 font-mono">{t.codigo}</p>
                  <div className="mt-1"><StockBadge cantidad={t.cantidadTotal} desglose={t.stockPorAlmacen} /></div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openImg(t)} className="text-purple-500"><Image size={16} /></button>
                  <button onClick={() => openEdit(t)} className="text-blue-600"><Pencil size={16} /></button>
                  <button onClick={() => deleteMutation.mutate(t.id)} className="text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-4">{editingId ? 'Editar Tarjeta' : 'Nueva Tarjeta'}</h3>
            <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                <input type="text" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saveMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                  {saveMutation.isPending ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImg && imgItem && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-1">Imagen de la Tarjeta</h3>
            <p className="text-sm text-gray-500 mb-5">{imgItem.nombre}</p>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!imgFile) { toast.error('Selecciona una imagen'); return; }
              const fd = new FormData();
              fd.append('imagen', imgFile);
              uploadMutation.mutate({ id: imgItem.id, formData: fd });
            }} className="space-y-5">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer hover:border-blue-400 transition-colors" onClick={() => imgInputRef.current?.click()}>
                {imgPreview ? (
                  <img src={imgPreview} alt="Preview" className="w-full max-h-40 object-contain rounded-lg" />
                ) : (
                  <><Image size={32} className="text-gray-300" /><span className="text-sm text-gray-400">Haz clic para seleccionar imagen</span></>
                )}
                {imgPreview && <span className="text-xs text-blue-600 hover:underline">Cambiar imagen</span>}
              </div>
              <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setImgFile(f); setImgPreview(URL.createObjectURL(f)); }
              }} />
              <div className="flex gap-3 justify-end pt-1">
                <button type="button" onClick={() => setShowImg(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={uploadMutation.isPending || !imgFile} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
                  {uploadMutation.isPending ? 'Subiendo...' : 'Guardar imagen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
